import { z } from "zod";
import { ValidZDate, ZDate } from "./date.ts";
import { DM } from "./math.ts";
import {
    Account,
    Address,
    AddressFamily,
    AddressFamilyMap,
    Amount,
    Balance,
    BlockNumber,
    Bool,
    BtcAddress,
    CaptureSurface,
    ChainKey,
    CountryAlpha2,
    CountryAlpha3,
    EVMAddress,
    EVMTokenAddress,
    FiatKey,
    Geolocation,
    GeolocationIssue,
    HttpsUrl,
    IAmountIssue,
    IAsset,
    IAssetAmount,
    IAssetDetails,
    IChain,
    IChainDetails,
    IChange,
    IChangeStructure,
    IEffect,
    IEffectStructure,
    IFiat,
    IFundingIssue,
    IIssue,
    Ip,
    IParkedFundWithdrawSource,
    IPaymentGist,
    IPiece,
    IPrices,
    IResource,
    IToken,
    ITokenAmount,
    Limits,
    Milliseconds,
    None,
    NonNegativeNumeric,
    OriginPattern,
    OtherIssue,
    OwnerIssue,
    PayinMethod,
    PayinMethodIssue,
    PayinMethodKey,
    PayoutMethod,
    Percentage,
    PieceInfo,
    PositiveNumeric,
    Price,
    Property,
    ProviderIssue,
    RevShareAddressMap,
    SafeUrl,
    Sau,
    Signature,
    SolAddress,
    StateAlpha2,
    Timezone,
    TokenKey,
    TonAddress,
    TronAddress,
    TxId,
    UnknownIssue,
    WalletPurpose,
    WithdrawAccount,
} from "./schema.ts";

export const IRequestForWallet = z.object({
    purpose: z.string().toUpperCase().pipe(WalletPurpose),
});
export type IRequestForWallet = z.infer<typeof IRequestForWallet>;

export const IWalletResult = z.object({
    evm: z.array(EVMAddress),
});
export type IWalletResult = z.infer<typeof IWalletResult>;

export const RampName = z.string().overwrite((name) => name.toLowerCase());
export type RampName = z.infer<typeof RampName>;

export const CountryCode = z.object({
    name: z.string(),
    alpha2: CountryAlpha2,
    alpha3: CountryAlpha3,
});
export type CountryCode = z.infer<typeof CountryCode>;

export const PurchaseStatus = z.enum([
    "INITIALIZED",
    "PAYMENT_SUBMITTED",
    "COMPLETED",
    "FAILED",
    "NOT_FOUND",
    "CANCELED",
]);
export type PurchaseStatus = z.infer<typeof PurchaseStatus>;

export const GeoFilter = z.object({
    alpha3_country_code: CountryAlpha3,
    state_code: StateAlpha2,
});
export type GeoFilter = z.infer<typeof GeoFilter>;

export const Flag = z.enum([
    "BETA_EDGES",
    "IN_HOUSE_EDGES",
    "KYC_DONOR",
    "ORG_BETA_EDGES",
    "ORG_EDGES",
]);
export type Flag = z.infer<typeof Flag>;

export const MAX_TAGS_TOTAL_LENGTH = 400;

export const Tags = z.array(z.string().min(1)).refine(
    (tags) => tags.reduce((total, tag) => total + tag.length, 0) <= MAX_TAGS_TOTAL_LENGTH,
    { message: `Tags must not exceed ${MAX_TAGS_TOTAL_LENGTH} characters in total` },
);
export type Tags = z.infer<typeof Tags>;

export const IRequestForAssets = z.object({
    sandbox: Bool.optional(),
    assets: z.array(IAsset).optional(),
});
export type IRequestForAssets = z.infer<typeof IRequestForAssets>;

export const IAssetsResults = z.record(IAsset, IAssetDetails);
export type IAssetsResults = z.infer<typeof IAssetsResults>;

export const IRequestForChains = z.object({
    sandbox: Bool.optional(),
});
export type IRequestForChains = z.infer<typeof IRequestForChains>;

export const IChainsResults = z.record(ChainKey, IChainDetails);
export type IChainsResults = z.infer<typeof IChainsResults>;

export const IRequestForInputs = z.object({
    sandbox: Bool.optional(),
    inputs: z.array(IAsset).optional(),
    outputs: z.array(IAsset),
    onramps: z.array(RampName).optional(),
    offramps: z.array(RampName).optional(),
    flags: z.array(Flag).optional(),
});
export type IRequestForInputs = z.infer<typeof IRequestForInputs>;

export const IRequestForOutputs = z.object({
    sandbox: Bool.optional(),
    inputs: z.array(IAsset),
    outputs: z.array(IAsset).optional(),
    onramps: z.array(RampName).optional(),
    offramps: z.array(RampName).optional(),
    flags: z.array(Flag).optional(),
});
export type IRequestForOutputs = z.infer<typeof IRequestForOutputs>;

export const FundingAction = z.enum(["TRANSFER", "CONTRACT_CALL", "SIGN"]);
export type FundingAction = z.infer<typeof FundingAction>;

export const ITokenTraits = z.object({
    funding_actions: z.array(FundingAction),
});
export type ITokenTraits = z.infer<typeof ITokenTraits>;

export const IInputsOutputsResult = z.object({
    fiats: z.array(IFiat),
    tokens: z.array(IToken),
    token_traits: z.record(IToken, ITokenTraits),
});
export type IInputsOutputsResult = z.infer<typeof IInputsOutputsResult>;

export const IInputsOutputsResults = z.record(IAsset, IInputsOutputsResult);
export type IInputsOutputsResults = z.infer<typeof IInputsOutputsResults>;

export const IPayinMethodsResponse = z.object({
    providers: z.record(z.string(), z.array(PayinMethod)),
    in_house: z.record(z.string(), z.array(PayinMethod)).optional(),
});
export type IPayinMethodsResponse = z.infer<typeof IPayinMethodsResponse>;

export const AddressUrl = z.string().superRefine((address, ctx) => {
    if (address.startsWith("mailto:")) {
        if (address.slice("mailto:".length).trim().length === 0) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, message: "mailto address must not be empty" });
        }
        return;
    }

    if (address.startsWith("tel:")) {
        if (address.slice("tel:".length).trim().length === 0) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, message: "tel address must not be empty" });
        }
        return;
    }

    ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "AddressUrl must use either mailto: or tel: scheme",
    });
});
export type AddressUrl = z.infer<typeof AddressUrl>;

export const Perms = z.object({
    includes: z.array(z.string()).optional(),
    excludes: z.array(z.string()).optional(),
});
export type Perms = z.infer<typeof Perms>;

export const InviteStatus = z.enum([
    "pending",
    "accepted",
    "declined",
    "cancelled",
]);
export type InviteStatus = z.infer<typeof InviteStatus>;

export const OrgFeature = z.string();
export type OrgFeature = z.infer<typeof OrgFeature>;

export class Constants {
    static readonly MAX_REV_SHARE_PCT = "2.5";
}

export const RevSharePercentage = Percentage.refine(
    (val) => Number(val) > 0 && Number(val) <= Number(Constants.MAX_REV_SHARE_PCT),
    {
        message: `Percentage must be between 0 and ${Constants.MAX_REV_SHARE_PCT}`,
    },
);
export type RevSharePercentage = z.infer<typeof RevSharePercentage>;

export const FeeClass = z.enum(["BANK", "CREDIT", "STABLE_USD", "VOLATILE"]);
export type FeeClass = z.infer<typeof FeeClass>;

export const BPS_DECIMALS = 2;
export const Bps = z.number().nonnegative().lt(10_000).refine(
    (bps) => DM.eq(bps.toString(), DM.trunc(bps.toString(), BPS_DECIMALS)),
    { message: `bps supports at most ${BPS_DECIMALS} decimal places` },
);
export type Bps = z.infer<typeof Bps>;

export const IChainFilter = z.union([
    z.array(ChainKey),
    z.object({
        include: z.array(ChainKey).optional(),
        exclude: z.array(ChainKey).optional(),
    }).strict(),
]);
export type IChainFilter = z.infer<typeof IChainFilter>;

export const ITokenSelector = z.discriminatedUnion("kind", [
    z.object({
        kind: z.literal("TOKEN"),
        token: IToken,
    }),
    z.object({
        kind: z.literal("REF"),
        ref: z.string(),
        chains: IChainFilter.optional(),
    }),
]);
export type ITokenSelector = z.infer<typeof ITokenSelector>;

export const IAssetSelector = z.discriminatedUnion("kind", [
    ...ITokenSelector.options,
    z.object({ kind: z.literal("FIAT"), fiat: IFiat }),
    z.object({ kind: z.literal("CHAIN"), chains: IChainFilter }),
]);
export type IAssetSelector = z.infer<typeof IAssetSelector>;

export const IFeeSelector = z.discriminatedUnion("kind", [
    ...IAssetSelector.options,
    z.object({ kind: z.literal("FEE_CLASS"), fee_class: FeeClass }),
]);
export type IFeeSelector = z.infer<typeof IFeeSelector>;

export const FeePairRelation = z.enum(["IDENTITY"]);
export type FeePairRelation = z.infer<typeof FeePairRelation>;

export const TakeBps = Bps.lte(500);
export type TakeBps = z.infer<typeof TakeBps>;

export const IFeeRule = z.object({
    input: IFeeSelector.optional(),
    output: IFeeSelector.optional(),
    relation: FeePairRelation.optional(),
    bps: TakeBps,
});
export type IFeeRule = z.infer<typeof IFeeRule>;

export const FeeSchedule = z.object({
    default_bps: TakeBps,
    rules: z.array(IFeeRule).optional(),
});
export type FeeSchedule = z.infer<typeof FeeSchedule>;

export const OrgConfig = z.object({
    allowed_flags: z.array(Flag).optional(),
    fee_schedule: FeeSchedule.optional(),
    gas_fee_subsidization: Percentage.optional(),
    request_concurrency: z.number().int().min(2).max(1000).optional(),
});
export type OrgConfig = z.infer<typeof OrgConfig>;

export const AllowedOrigin = z.object({
    pattern: z.string(),
    created_by: z.string().nullable(),
    created_at: ZDate,
    reviewed_at: ZDate.nullable(),
});
export type AllowedOrigin = z.infer<typeof AllowedOrigin>;

export const OrgSettings = z.object({
    display_name: z.string().optional(),
    allowed_origins: z.array(AllowedOrigin).optional(),
});
export type OrgSettings = z.infer<typeof OrgSettings>;

export const OrgId = z.uuid();
export type OrgId = z.infer<typeof OrgId>;

export const OrgInviteId = z.uuid();
export type OrgInviteId = z.infer<typeof OrgInviteId>;

export const OrgMemberId = z.uuid();
export type OrgMemberId = z.infer<typeof OrgMemberId>;

export const IOrgMsa = z.object({
    signer_name: z.string(),
    signer_email: z.string().email(),
    signer_title: z.string().optional(),
    attribution: z.string().optional(),
});
export type IOrgMsa = z.infer<typeof IOrgMsa>;

export const OrgMsaAcceptance = IOrgMsa.extend({
    version: z.string().min(1),
});
export type OrgMsaAcceptance = z.infer<typeof OrgMsaAcceptance>;

export const OrgMsa = IOrgMsa.extend({
    accepted_at: z.record(z.string().min(1), ZDate),
});
export type OrgMsa = z.infer<typeof OrgMsa>;

export const ICreateOrgRequest = z.object({
    name: z.string().trim().min(1),
    msa: OrgMsaAcceptance.optional(),
});
export type ICreateOrgRequest = z.infer<typeof ICreateOrgRequest>;

export const ICreateOrgResponse = z.object({
    id: OrgId,
    name: z.string(),
});
export type ICreateOrgResponse = z.infer<typeof ICreateOrgResponse>;

export const ICreateAPIKeyRequest = z.object({
    label: z.string(),
    perms: Perms,
});
export type ICreateAPIKeyRequest = z.infer<typeof ICreateAPIKeyRequest>;

export const ICreateAPIKeyResponse = z.object({
    label: z.string(),
    public_key: z.string(),
    secret_key: z.string(),
});
export type ICreateAPIKeyResponse = z.infer<typeof ICreateAPIKeyResponse>;

export const IUpdateAPIKeyRequest = z.object({
    public_key: z.string(),
    label: z.string().optional(),
    perms: Perms.optional(),
});
export type IUpdateAPIKeyRequest = z.infer<typeof IUpdateAPIKeyRequest>;

export const IDeleteAPIKeyRequest = z.object({
    public_key: z.string(),
});
export type IDeleteAPIKeyRequest = z.infer<typeof IDeleteAPIKeyRequest>;

export const IGetAPIKeysResponse = z.array(z.object({
    label: z.string().nullable(),
    public_key: z.string(),
    perms: Perms,
    created_at: ZDate,
}));
export type IGetAPIKeysResponse = z.infer<typeof IGetAPIKeysResponse>;

export const IGetOrgMembersRequest = z.object({
    user_id: OrgMemberId.optional(),
});
export type IGetOrgMembersRequest = z.infer<typeof IGetOrgMembersRequest>;

export const IGetOrgMembersResponse = z.array(z.object({
    org_id: OrgId,
    user_id: OrgMemberId,
    created_at: ZDate,
    updated_at: ZDate,
    perms: Perms,
    user_address: AddressUrl,
}));
export type IGetOrgMembersResponse = z.infer<typeof IGetOrgMembersResponse>;

export const IUpdateOrgMemberPermsRequest = z.object({
    user_id: OrgMemberId,
    perms: Perms,
});
export type IUpdateOrgMemberPermsRequest = z.infer<
    typeof IUpdateOrgMemberPermsRequest
>;

export const IDeleteOrgMemberRequest = z.object({
    user_id: OrgMemberId,
});
export type IDeleteOrgMemberRequest = z.infer<typeof IDeleteOrgMemberRequest>;

export const IGetOrgsResponse = z.array(z.object({
    id: OrgId,
    name: z.string(),
    settings: OrgSettings,
    created_at: ZDate,
}));
export type IGetOrgsResponse = z.infer<typeof IGetOrgsResponse>;

export const MyOrgsPaginationKey = z.string().transform((val, ctx) => {
    const [org_id, iso_timestamp] = val.split("_");

    if (!iso_timestamp || !org_id) {
        ctx.addIssue({
            code: "custom",
            message: "Pagination key must contain org_id and iso_timestamp separated by '_'",
        });
        return z.NEVER;
    }
    const parsed_org_id = OrgId.safeParse(org_id);
    if (!parsed_org_id.success) {
        ctx.addIssue({
            code: "custom",
            message: "Invalid org_id in pagination key",
        });
        return z.NEVER;
    }

    const timestamp = new Date(iso_timestamp);
    if (isNaN(timestamp.getTime())) {
        ctx.addIssue({
            code: "custom",
            message: "Invalid ISO timestamp in pagination key",
        });
        return z.NEVER;
    }

    return { timestamp, org_id: parsed_org_id.data };
});
export type MyOrgsPaginationKey = z.infer<typeof MyOrgsPaginationKey>;

export const IGetMyOrgsRequest = z.object({
    limit: z.coerce.number().int().positive().default(25),
    pagination_key: MyOrgsPaginationKey.optional(),
});
export type IGetMyOrgsRequest = z.infer<typeof IGetMyOrgsRequest>;

export const CommitPeriod = z.enum(["MONTH", "QUARTER", "YEAR"]);
export type CommitPeriod = z.infer<typeof CommitPeriod>;

export const CommitStatus = z.enum(["ACTIVE", "TERMINATED", "DEFAULTED", "EXPIRED"]);
export type CommitStatus = z.infer<typeof CommitStatus>;

export const CommitRateBound = z.object({
    input: FeeClass,
    output: FeeClass,
    max_bps: Bps,
    min_bps: Bps.optional(),
}).refine((bound) => bound.min_bps === undefined || bound.min_bps <= bound.max_bps, {
    message: "min_bps must not exceed max_bps",
});
export type CommitRateBound = z.infer<typeof CommitRateBound>;

export const OrgCommitContract = z.object({
    status: CommitStatus,
    period: CommitPeriod,
    rate_bounds: z.array(CommitRateBound),
    term_start: ZDate,
    term_end: ZDate,
});
export type OrgCommitContract = z.infer<typeof OrgCommitContract>;

export const IGetMyOrgsResponse = z.object({
    orgs: z.array(z.object({
        id: OrgId,
        name: z.string(),
        settings: OrgSettings,
        membership: z.object({
            user_id: OrgMemberId,
            user_address: AddressUrl,
            member_since: ZDate,
            updated_at: ZDate,
            effective_permissions: z.array(
                z.object({ key: z.string(), description: z.string() }),
            ),
        }),
        commit_contract: OrgCommitContract.optional(),
    })),
    next_pagination_key: z.string().nullable(),
});
export type IGetMyOrgsResponse = z.infer<typeof IGetMyOrgsResponse>;

export const ICreateOrgInviteRequest = z.object({
    invitee_address: z.string(),
    perms: Perms,
});
export type ICreateOrgInviteRequest = z.infer<typeof ICreateOrgInviteRequest>;

export const IGetMyOrgInvitesRequest = z.object({
    statuses: InviteStatus.array().optional(),
});
export type IGetMyOrgInvitesRequest = z.infer<typeof IGetMyOrgInvitesRequest>;

export const IGetOrgInvitesRequest = z.object({
    statuses: InviteStatus.array().optional(),
});
export type IGetOrgInvitesRequest = z.infer<typeof IGetOrgInvitesRequest>;

export const IGetOrgInvitesResponse = z.array(z.object({
    id: OrgInviteId,
    org_id: OrgId,
    org_name: z.string(),
    org_settings: OrgSettings,
    // XXX: deprecated. remove after fe migrates
    inviter: z.string(),
    inviter_address: z.string().nullable(),
    invitee_address: AddressUrl,
    status: InviteStatus,
    created_at: ZDate,
    updated_at: ZDate,
}));
export type IGetOrgInvitesResponse = z.infer<typeof IGetOrgInvitesResponse>;

export const IUpdateInviteRequest = z.object({
    id: OrgInviteId,
    status: InviteStatus,
});
export type IUpdateInviteRequest = z.infer<typeof IUpdateInviteRequest>;

export const IDeleteInviteRequest = z.object({
    id: OrgInviteId,
});
export type IDeleteInviteRequest = z.infer<typeof IDeleteInviteRequest>;

export const IUpdateOrgOwnerRequest = z.object({
    user_id: OrgMemberId,
});
export type IUpdateOrgOwnerRequest = z.infer<typeof IUpdateOrgOwnerRequest>;

export const IDisableOrgRequest = z.object({
    id: OrgId,
});
export type IDisableOrgRequest = z.infer<typeof IDisableOrgRequest>;

export const IUpdateOrgRequest = z.object({
    name: z.string().trim().min(1).optional(),
    settings: OrgSettings.omit({ allowed_origins: true }).optional(),
}).refine((data) => Object.keys(data).length > 0, {
    message: "At least one field must be provided",
});
export type IUpdateOrgRequest = z.infer<typeof IUpdateOrgRequest>;

export const ICreateRevShareRequest = z.object({
    name: z.string(),
    percentage: RevSharePercentage,
    preferred_tokens: z.array(IToken),
    addresses: AddressFamilyMap.partial(),
    allow_arbitrary: z.boolean(),
});
export type ICreateRevShareRequest = z.infer<typeof ICreateRevShareRequest>;

export const IUpdateRevShareRequest = z.object({
    name: z.string(),
    percentage: RevSharePercentage.optional(),
    preferred_tokens: z.array(IToken).optional(),
    addresses: AddressFamilyMap.partial().optional(),
    allow_arbitrary: z.boolean().optional(),
});
export type IUpdateRevShareRequest = z.infer<typeof IUpdateRevShareRequest>;

export const IRevShare = z.object({
    id: z.uuid(),
    org_id: OrgId,
    name: z.string(),
    percentage: Amount,
    preferred_tokens: z.array(IToken),
    addresses: AddressFamilyMap.partial(),
    allow_arbitrary: z.boolean(),
    created_by: z.string(),
    created_at: ZDate,
    updated_at: ZDate,
});
export type IRevShare = z.infer<typeof IRevShare>;

export const IGetRevSharesRequest = z.object({
    name: z.string().optional(),
});
export type IGetRevSharesRequest = z.infer<typeof IGetRevSharesRequest>;

export const IDeleteRevShareRequest = z.object({
    name: z.string(),
});
export type IDeleteRevShareRequest = z.infer<typeof IDeleteRevShareRequest>;

export const IGetRevSharesResponse = z.array(IRevShare);
export type IGetRevSharesResponse = z.infer<typeof IGetRevSharesResponse>;

export const ICreateRevShareResponse = IRevShare;
export type ICreateRevShareResponse = z.infer<typeof ICreateRevShareResponse>;

export const IUpdateRevShareResponse = IRevShare;
export type IUpdateRevShareResponse = z.infer<typeof IUpdateRevShareResponse>;

export const IRevShareProfile = z.object({
    name: z.string(),
    percentage: Amount,
    preferred_tokens: z.array(IToken),
    addresses: AddressFamilyMap.partial(),
    allow_arbitrary: z.boolean(),
});
export type IRevShareProfile = z.infer<typeof IRevShareProfile>;

export const IFeeSponsorshipPolicy = z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("PERCENTAGE"), percentage: Percentage }),
    z.object({ kind: z.literal("PAR") }),
]);
export type IFeeSponsorshipPolicy = z.infer<typeof IFeeSponsorshipPolicy>;

export const IFeeSponsorshipChainFilter = z.union([
    z.array(ChainKey),
    z.object({
        include: z.array(ChainKey).optional(),
        exclude: z.array(ChainKey).optional(),
    }).strict(),
]);
export type IFeeSponsorshipChainFilter = z.infer<typeof IFeeSponsorshipChainFilter>;

export const IFeeSponsorshipAssetSelector = z.discriminatedUnion("kind", [
    z.object({
        kind: z.literal("ASSET"),
        asset: IAsset,
    }),
    z.object({
        kind: z.literal("REF"),
        ref: z.string(),
        chains: IFeeSponsorshipChainFilter.optional(),
    }),
]);
export type IFeeSponsorshipAssetSelector = z.infer<typeof IFeeSponsorshipAssetSelector>;

export const IFeeSponsorshipRoute = z.object({
    input: IFeeSponsorshipAssetSelector,
    output: IToken,
});
export type IFeeSponsorshipRoute = z.infer<typeof IFeeSponsorshipRoute>;

export const IFeeSponsorship = z.object({
    id: z.uuid(),
    org_id: OrgId,
    name: z.string(),
    enabled: z.boolean(),
    policy: IFeeSponsorshipPolicy,
    routes: z.array(IFeeSponsorshipRoute),
    max_fee_sponsorship_fiat: Amount.nullable().optional(),
    min_input_fiat: Amount.nullable().optional(),
    created_by: z.string(),
    created_at: ZDate,
    updated_at: ZDate,
});
export type IFeeSponsorship = z.infer<typeof IFeeSponsorship>;

export const ICreateFeeSponsorshipRequest = z.object({
    name: z.string(),
    enabled: z.boolean(),
    policy: IFeeSponsorshipPolicy,
    routes: z.array(IFeeSponsorshipRoute),
    max_fee_sponsorship_fiat: Amount.nullable().optional(),
    min_input_fiat: Amount.nullable().optional(),
});
export type ICreateFeeSponsorshipRequest = z.infer<
    typeof ICreateFeeSponsorshipRequest
>;

export const IUpdateFeeSponsorshipRequest = z.object({
    name: z.string(),
    enabled: z.boolean().optional(),
    policy: IFeeSponsorshipPolicy.optional(),
    routes: z.array(IFeeSponsorshipRoute).optional(),
    max_fee_sponsorship_fiat: Amount.nullable().optional(),
    min_input_fiat: Amount.nullable().optional(),
});
export type IUpdateFeeSponsorshipRequest = z.infer<
    typeof IUpdateFeeSponsorshipRequest
>;

export const IGetFeeSponsorshipsRequest = z.object({
    name: z.string().optional(),
});
export type IGetFeeSponsorshipsRequest = z.infer<
    typeof IGetFeeSponsorshipsRequest
>;

export const IDeleteFeeSponsorshipRequest = z.object({
    name: z.string(),
});
export type IDeleteFeeSponsorshipRequest = z.infer<
    typeof IDeleteFeeSponsorshipRequest
>;

export const IGetFeeSponsorshipsResponse = z.array(IFeeSponsorship);
export type IGetFeeSponsorshipsResponse = z.infer<
    typeof IGetFeeSponsorshipsResponse
>;

export const ICreateFeeSponsorshipResponse = IFeeSponsorship;
export type ICreateFeeSponsorshipResponse = z.infer<
    typeof ICreateFeeSponsorshipResponse
>;

export const IUpdateFeeSponsorshipResponse = IFeeSponsorship;
export type IUpdateFeeSponsorshipResponse = z.infer<
    typeof IUpdateFeeSponsorshipResponse
>;

export const IFeeSponsorshipAvailableAssetsRequest = z.object({
    inputs: z.array(IAsset).optional(),
    outputs: z.array(IToken).optional(),
});
export type IFeeSponsorshipAvailableAssetsRequest = z.infer<
    typeof IFeeSponsorshipAvailableAssetsRequest
>;

export const IFeeSponsorshipConfig = z.object({
    name: z.string(),
    policy: IFeeSponsorshipPolicy,
    routes: z.array(IFeeSponsorshipRoute),
    max_fee_sponsorship_fiat: Amount.nullable().optional(),
    min_input_fiat: Amount.nullable().optional(),
});
export type IFeeSponsorshipConfig = z.infer<typeof IFeeSponsorshipConfig>;

export const IFeeSponsorshipGrantPayload = z.object({
    public_key: z.string(),
    fee_sponsorship_name: z.string(),
    max_fee_sponsorship_fiat: Amount.nullable().optional(),
    min_input_fiat: Amount.nullable().optional(),
    jti: z.uuid(),
    exp: z.number(),
});
export type IFeeSponsorshipGrantPayload = z.infer<
    typeof IFeeSponsorshipGrantPayload
>;

export const IFeeSponsorshipGrant = z.object({
    grant_payload: z.string(),
    grant_signature: z.string(),
});
export type IFeeSponsorshipGrant = z.infer<typeof IFeeSponsorshipGrant>;

export const IFeeSponsorshipAuth = z.object({
    public_key: z.string(),
    grant_jti: z.uuid(),
    exp: z.number(),
    max_fee_sponsorship_fiat: Amount.nullable().optional(),
    min_input_fiat: Amount.nullable().optional(),
});
export type IFeeSponsorshipAuth = z.infer<typeof IFeeSponsorshipAuth>;

export const IFeeSponsorshipAmount = z.object({
    token: IToken,
    sau: Sau,
    usd_amount: Amount,
});
export type IFeeSponsorshipAmount = z.infer<
    typeof IFeeSponsorshipAmount
>;

export const IOrgSponsorshipWalletSnapshot = z.object({
    id: z.uuid(),
    address_family: AddressFamily,
    address: Address,
});
export type IOrgSponsorshipWalletSnapshot = z.infer<
    typeof IOrgSponsorshipWalletSnapshot
>;

export const IOrgSponsorshipWallet = z.object({
    id: z.uuid(),
    org_id: OrgId,
    address_family: AddressFamily,
    address: Address,
    created_by: z.string(),
    created_at: ZDate,
    updated_at: ZDate,
});
export type IOrgSponsorshipWallet = z.infer<typeof IOrgSponsorshipWallet>;

export const ICreateOrgSponsorshipWalletRequest = z.object({
    address_family: AddressFamily.optional(),
});
export type ICreateOrgSponsorshipWalletRequest = z.infer<
    typeof ICreateOrgSponsorshipWalletRequest
>;

export const ICreateOrgSponsorshipWalletResponse = IOrgSponsorshipWallet;
export type ICreateOrgSponsorshipWalletResponse = z.infer<
    typeof ICreateOrgSponsorshipWalletResponse
>;

export const IGetOrgSponsorshipWalletsResponse = z.array(IOrgSponsorshipWallet);
export type IGetOrgSponsorshipWalletsResponse = z.infer<
    typeof IGetOrgSponsorshipWalletsResponse
>;

export const IFeeSponsorshipStatus = z.enum([
    "PENDING",
    "IN_PROGRESS",
    "APPLIED",
    "FAILED",
]);
export type IFeeSponsorshipStatus = z.infer<typeof IFeeSponsorshipStatus>;

export const IFeeSponsorshipState = z.object({
    status: IFeeSponsorshipStatus,
    attempted_at: ZDate.optional(),
    completed_at: ZDate.optional(),
    failed_at: ZDate.optional(),
    tx_hash: z.string().optional(),
    reason: z.string().optional(),
});
export type IFeeSponsorshipState = z.infer<typeof IFeeSponsorshipState>;

export const IFeeSponsorshipProfileSnapshot = z.object({
    name: z.string(),
    policy: IFeeSponsorshipPolicy,
    max_fee_sponsorship_fiat: Amount.nullable().optional(),
    min_input_fiat: Amount.nullable().optional(),
});
export type IFeeSponsorshipProfileSnapshot = z.infer<
    typeof IFeeSponsorshipProfileSnapshot
>;

export const IFeeSponsorshipData = z.object({
    profile: IFeeSponsorshipProfileSnapshot,
    auth: IFeeSponsorshipAuth,
    fee_sponsorship: IFeeSponsorshipAmount,
    state: IFeeSponsorshipState,
    wallet: IOrgSponsorshipWalletSnapshot,
});
export type IFeeSponsorshipData = z.infer<typeof IFeeSponsorshipData>;

export const ISponsorship = z.object({
    name: z.string(),
});
export type ISponsorship = z.infer<typeof ISponsorship>;

export const IUpdateOrgCreditRequest = z.object({
    org_id: OrgId,
    credit: Sau,
    sandbox: z.boolean(),
});
export type IUpdateOrgCreditRequest = z.infer<typeof IUpdateOrgCreditRequest>;

export const IFeeSponsorshipAnalyticsRequest = z.object({
    sandbox: z.boolean().optional(),
});
export type IFeeSponsorshipAnalyticsRequest = z.infer<
    typeof IFeeSponsorshipAnalyticsRequest
>;

export const IFeeSponsorshipAnalyticsResponse = z.object({
    total_fee_sponsored_fiat: Amount,
    total_sponsored_payments: z.number(),
    by_profile: z.record(
        z.string(),
        z.object({
            count: z.number(),
            total_fee_sponsored_fiat: Amount,
        }),
    ),
});
export type IFeeSponsorshipAnalyticsResponse = z.infer<
    typeof IFeeSponsorshipAnalyticsResponse
>;

export const IFeeSponsorshipBalanceRequest = z.object({
    sandbox: z.boolean().optional(),
});
export type IFeeSponsorshipBalanceRequest = z.infer<
    typeof IFeeSponsorshipBalanceRequest
>;

export const IFeeSponsorshipTokenBalance = z.object({
    token: TokenKey,
    symbol: z.string(),
    native: z.boolean(),
    amount: Amount,
    fiat: Amount.nullable(),
});
export type IFeeSponsorshipTokenBalance = z.infer<
    typeof IFeeSponsorshipTokenBalance
>;

export const IFeeSponsorshipWalletBalance = z.object({
    wallet_id: z.uuid(),
    address_family: AddressFamily,
    address: z.string(),
    balances: z.array(IFeeSponsorshipTokenBalance),
});
export type IFeeSponsorshipWalletBalance = z.infer<
    typeof IFeeSponsorshipWalletBalance
>;

export const IFeeSponsorshipProfileBalance = z.object({
    profile_name: z.string(),
    enabled: z.boolean(),
    payments_this_period: z.number(),
    fee_sponsored_this_period_fiat: Amount,
    balances: z.array(IFeeSponsorshipTokenBalance),
});
export type IFeeSponsorshipProfileBalance = z.infer<
    typeof IFeeSponsorshipProfileBalance
>;

export const IFeeSponsorshipBalanceResponse = z.object({
    period: z.string(),
    currency: FiatKey,
    sandbox: z.boolean(),
    available_balance_fiat: Amount,
    available_balance_fiat_partial: z.boolean(),
    sponsored_fees_this_period_fiat: Amount,
    wallets: z.array(IFeeSponsorshipWalletBalance),
    by_profile: z.array(IFeeSponsorshipProfileBalance),
});
export type IFeeSponsorshipBalanceResponse = z.infer<
    typeof IFeeSponsorshipBalanceResponse
>;

export const IUpdateOrgFeaturesRequest = z.object({
    org_id: OrgId,
    features: z.array(OrgFeature),
});
export type IUpdateOrgFeaturesRequest = z.infer<
    typeof IUpdateOrgFeaturesRequest
>;

export const IUpdateOrgConfigRequest = z.object({
    org_id: OrgId,
    config: OrgConfig,
});
export type IUpdateOrgConfigRequest = z.infer<typeof IUpdateOrgConfigRequest>;

export const IAddOrgDomainsRequest = z.object({
    domains: z.array(OriginPattern).min(1),
});
export type IAddOrgDomainsRequest = z.infer<typeof IAddOrgDomainsRequest>;

export const IDeleteOrgDomainsRequest = z.object({
    domains: z.array(OriginPattern).min(1),
});
export type IDeleteOrgDomainsRequest = z.infer<typeof IDeleteOrgDomainsRequest>;

export const IOrgDomainsResponse = z.object({
    allowed_origins: z.array(AllowedOrigin),
});
export type IOrgDomainsResponse = z.infer<typeof IOrgDomainsResponse>;

export const IGetOrgAdminDomainsResponse = z.array(z.object({
    org_id: OrgId,
    org_name: z.string(),
    domains: z.array(AllowedOrigin),
}));
export type IGetOrgAdminDomainsResponse = z.infer<
    typeof IGetOrgAdminDomainsResponse
>;

export const IReviewOrgDomainsRequest = z.object({
    org_id: OrgId,
    approve: z.boolean(),
    domains: z.array(OriginPattern).min(1),
});
export type IReviewOrgDomainsRequest = z.infer<typeof IReviewOrgDomainsRequest>;

export const IGetOrgInfoResponse = z.object({
    id: OrgId,
    name: z.string(),
    allow_fiat: z.boolean().optional(),
    settings: OrgSettings,
    created_at: ZDate,
    features: z.array(OrgFeature),
    config: OrgConfig.optional(),
    msa: OrgMsa.nullable(),
    commit_contract: OrgCommitContract.optional(),
});
export type IGetOrgInfoResponse = z.infer<typeof IGetOrgInfoResponse>;

export const IRequestForPrices = z.object({
    fiat: IFiat,
    tokens: z.array(IToken),
});
export type IRequestForPrices = z.infer<typeof IRequestForPrices>;

export const IRequestForSPWInfo = z.object({
    spw_address: z.string(),
    chains: z.array(IChain).optional(),
});
export type IRequestForSPWInfo = z.infer<typeof IRequestForSPWInfo>;

export const IChainInfo = z.object({
    chain: IChain,
    counterfactual_exists: z.boolean(),
});
export type IChainInfo = z.infer<typeof IChainInfo>;

export const ISPWInfo = z.object({
    workflow_id: z.string(),
    chain_infos: z.array(IChainInfo),
});
export type ISPWInfo = z.infer<typeof ISPWInfo>;

export const ISPWInfoResponse = ISPWInfo.nullable();
export type ISPWInfoResponse = z.infer<typeof ISPWInfoResponse>;

export const IRequestForUpdateLegacy = z.object({
    workflow_ids: z.array(z.uuid()).optional(),
    verification_code: z.string(),
});
export type IRequestForUpdateLegacy = z.infer<typeof IRequestForUpdateLegacy>;

export const IChangeAmount = IChangeStructure.extend({
    amount: Amount,
});
export type IChangeAmount = z.infer<typeof IChangeAmount>;

export const IEffectAmount = IEffectStructure.extend({
    consume: z.array(IChangeAmount),
    produce: z.array(IChangeAmount),
});
export type IEffectAmount = z.infer<typeof IEffectAmount>;

export const IFulfilledChangeAmount = IChangeAmount.extend({
    tx_id: z.string().optional(),
});
export type IFulfilledChangeAmount = z.infer<typeof IFulfilledChangeAmount>;

export const IFulfilledEffectAmount = z.object({
    consume: z.array(IFulfilledChangeAmount),
    produce: z.array(IFulfilledChangeAmount),
});
export type IFulfilledEffectAmount = z.infer<typeof IFulfilledEffectAmount>;

export const TerminalObservedStatus = z.union([
    z.literal("COMPLETE"),
    z.literal("UNREACHABLE"),
    z.literal("FAILED"),
]);
export type TerminalObservedStatus = z.infer<typeof TerminalObservedStatus>;

export const NonTerminalObservedStatus = z.union([
    z.literal("PENDING"),
    z.literal("ATTEMPT_FAILED"),
]);
export type NonTerminalObservedStatus = z.infer<
    typeof NonTerminalObservedStatus
>;

export const ObservedStatus = z.union([
    TerminalObservedStatus,
    NonTerminalObservedStatus,
]);
export type ObservedStatus = z.infer<typeof ObservedStatus>;

export const IFailureContent = z.object({
    service_ids: z.array(z.string()),
    latency_seconds: z.number(),
    issues: z.optional(z.array(IIssue)),
});
export type IFailureContent = z.infer<typeof IFailureContent>;

export const WorkflowOverallStatusStored = z.union([
    z.literal("PENDING"),
    z.literal("COMPLETE"),
    z.literal("FAILED"),
    z.literal("EXPIRED"),
]);
export type WorkflowOverallStatusStored = z.infer<
    typeof WorkflowOverallStatusStored
>;

export const WorkflowOverallStatus = z.union([
    ...WorkflowOverallStatusStored.options,
    z.literal("WITHDRAWN"),
    z.literal("TAINTED"),
    z.literal("UNCONFIRMED"),
    z.literal("WITHDRAW_PENDING"),
]);
export type WorkflowOverallStatus = z.infer<typeof WorkflowOverallStatus>;

export const IFees = z.object({
    total_fees: Amount,
    conversion_fees: Amount,
    network_fees: Amount,
    business_fees: Amount,
    currency_symbol: IFiat,
    fee_sponsorship: Amount,
});
export type IFees = z.infer<typeof IFees>;

export const IFixedInputQuoteRequest = z.object({
    kind: z.literal("FIXED_INPUT"),
    fixed_input_amount: IAssetAmount,
    output_asset: IAsset,
    funder_fee: Amount.optional(),
    custom_actions: z.any().optional(),
    dest_address: Address.optional(),
});
export type IFixedInputQuoteRequest = z.infer<typeof IFixedInputQuoteRequest>;

export const RDWTransferMode = z.enum(["FIXED_AMOUNT"]);
export type RDWTransferMode = z.infer<typeof RDWTransferMode>;

export const StepType = z.enum(["ONRAMP", "ONCHAIN_STEP", "USER_FUND", "FEE_SPONSORSHIP", "RDW_TRANSFER", "OFFRAMP"]);
export type StepType = z.infer<typeof StepType>;

export const IRouteItem = z.object({
    type: StepType,
    net_effect: IEffectAmount,
    pieces_info: z.array(PieceInfo),
    step_index: z.number().optional(),
    custom_actions_input_amount: Amount.optional(),
    in_house: z.boolean().optional(),
});
export type IRouteItem = z.infer<typeof IRouteItem>;

export const IQuotedRouteItem = IRouteItem;
export type IQuotedRouteItem = IRouteItem;

export const FulfilledStatus = z.union([ObservedStatus]);
export type FulfilledStatus = z.infer<typeof FulfilledStatus>;

export const IFulfilledRouteItem = IRouteItem.extend({
    status: FulfilledStatus,
    net_effect: IFulfilledEffectAmount,
    transaction_hash: z.string().optional(),
    superseded: z.boolean().optional(),
});
export type IFulfilledRouteItem = z.infer<typeof IFulfilledRouteItem>;

export const IQuoteRequest = z.object({
    request: z.discriminatedUnion("kind", [IFixedInputQuoteRequest]),
    price_currency: IFiat,
    allow_rdw: z.boolean().optional(),
    onramps: z.array(RampName).optional(),
    onramp_methods: z.array(PayinMethod).optional(),
    offramps: z.array(RampName).optional(),
    offramp_methods: z.array(PayoutMethod).optional(),
    payment_method_ref: z.string().optional(),
    customer_ip_address: Ip.optional(),
    customer_id: z.string().optional(),
    label: z.string().max(255).optional(),
    tags: Tags.optional(),
    parent_payment_id: z.uuid().optional(),
    flags: z.array(Flag).optional(),
    in_house_only: z.boolean().optional(),
    rev_share_name: z.string().optional(),
    fee_sponsorship: IFeeSponsorshipGrant.optional(),
    // Only intended for internal use
    allow_unsafe_transfer_out: z.boolean().optional(),
    show_all_issues: z.boolean().optional(),
    allow_exceed_max_input_limit: z.boolean().optional(),
    allow_unsafe_slippage: z.boolean().optional(),
    use_intent_refund: z.boolean().optional(),
    hops: z.array(z.string()).optional(),
});
export type IQuoteRequest = z.infer<typeof IQuoteRequest>;

export const IPayinMethodsRequest = z.object({
    input: IFiat,
    output: IAsset,
    customer_ip_address: Ip.optional(),
    flags: z.array(Flag).optional(),
});
export type IPayinMethodsRequest = z.infer<typeof IPayinMethodsRequest>;

export const IGeolocationRequest = z.object({
    customer_ip_address: Ip.optional(),
});
export type IGeolocationRequest = z.infer<typeof IGeolocationRequest>;

export const IGeolocationInfo = z.object({
    alpha3_country_code: CountryAlpha3,
    alpha2_country_code: CountryAlpha2,
    state_code: z.union([StateAlpha2, z.literal("")]),
    default_currency: z.string().optional(),
});
export type IGeolocationInfo = z.infer<typeof IGeolocationInfo>;

export const IGeolocationResponse = IGeolocationInfo.nullable();
export type IGeolocationResponse = z.infer<typeof IGeolocationResponse>;

export const IEntry = z.object({
    fees: IFees.optional(),
    output_amount: IAssetAmount.optional(),
    onramp: RampName.optional(),
    onramp_method: PayinMethod.optional(),
    offramp: RampName.optional(),
    offramp_method: PayoutMethod.optional(),
    payment_method_ref: z.string().optional(),
});
export type IEntry = z.infer<typeof IEntry>;

export const IFulfilledEntry = IEntry.extend({
    route: z.array(IFulfilledRouteItem),
});
export type IFulfilledEntry = z.infer<typeof IFulfilledEntry>;

export const IQuotedEntry = IEntry.extend({
    fees: IFees,
    output_amount: IAssetAmount,
    route: z.array(IQuotedRouteItem),
    latency_seconds: z.number().optional(),
    sponsorship: ISponsorship.optional(),
});
export type IQuotedEntry = z.infer<typeof IQuotedEntry>;

export const ISupersedablePaymentSummary = z.object({
    payment_id: z.string(),
    quoted: IQuotedEntry,
    payout_destination: z.object({
        payout_method: PayoutMethod.optional(),
        beneficiary: z.string().optional(),
        account_last4: z.string().optional(),
    }).nullable().optional(),
    destination_address: Address.nullable(),
});
export type ISupersedablePaymentSummary = z.infer<typeof ISupersedablePaymentSummary>;

export const IQuote = IQuotedEntry.extend({
    payment_id: z.string(),
    funding_action: FundingAction,
    supersedable_payment_summaries: z.array(ISupersedablePaymentSummary).optional(),
});
export type IQuote = z.infer<typeof IQuote>;

export const IQuoteResponse = z.object({
    quote_request: IQuoteRequest,
    quotes: z.array(IQuote),
    current_prices: IPrices,
    price_currency: IFiat,
    state_token: z.string(),
    quoted_at: ZDate,
    accept_by: ZDate,
    fault: IIssue.nullable(),
    failures: z.array(IFailureContent),
    limits: z.array(Limits),
});
export type IQuoteResponse = z.infer<typeof IQuoteResponse>;

export const IRequestForPaymentsPaginationKey = z.string().transform(
    (val, ctx) => {
        const [workflow_id, iso_timestamp] = val.split("_");

        if (!iso_timestamp || !workflow_id) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "Pagination key must contain workflow_id and iso_timestamp separated by '_'",
            });
            return z.NEVER;
        }

        const timestamp = new Date(iso_timestamp);
        if (isNaN(timestamp.getTime())) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "Invalid ISO timestamp in pagination key",
            });
            return z.NEVER;
        }

        if (workflow_id.length !== 36) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "Invalid workflow_id in pagination key",
            });
            return z.NEVER;
        }

        return {
            timestamp,
            workflow_id,
        };
    },
);
export type IRequestForPaymentsPaginationKey = z.infer<
    typeof IRequestForPaymentsPaginationKey
>;

export const PaymentCategory = z.enum(["ALL", "NEW_OR_FUNDED", "FUNDED", "DONE", "STUCK"]);
export type PaymentCategory = z.infer<typeof PaymentCategory>;

export const PaymentSortColumn = z.enum([
    "DATE",
    "AMOUNT_USD",
    "COUNTRY",
    "STATUS",
    "SENT_TOKEN",
    "RECEIVED_TOKEN",
    "SENT_CHAIN",
    "RECEIVED_CHAIN",
]);
export type PaymentSortColumn = z.infer<typeof PaymentSortColumn>;

export const PaymentSortDirection = z.enum(["ASC", "DESC"]);
export type PaymentSortDirection = z.infer<typeof PaymentSortDirection>;

export const PaymentSortSpec = z.string().transform((s, ctx) => {
    const parts = s.split(":");
    if (
        parts.length !== 2 ||
        !PaymentSortColumn.options.includes(parts[0] as PaymentSortColumn) ||
        !PaymentSortDirection.options.includes(parts[1] as PaymentSortDirection)
    ) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `invalid sort_by entry "${s}"`,
        });
        return z.NEVER;
    }
    return {
        column: parts[0] as PaymentSortColumn,
        direction: parts[1] as PaymentSortDirection,
    };
});
export type PaymentSortSpec = z.infer<typeof PaymentSortSpec>;

export const IPaymentsFiltersBase = z.object({
    id_query: z.string().optional(),
    dest_address: Address.optional(),
    owner_address: Address.optional(),
    limit: PositiveNumeric.default(25),
    // XXX: Remove category and give categories a default of ["NEW_OR_FUNDED"] once clients have migrated.
    // At that point ALL can also be removed from PaymentCategory since its only purpose is to opt out of that default.
    category: PaymentCategory.default("NEW_OR_FUNDED"), // @deprecated — use categories instead
    categories: z.array(PaymentCategory).optional(),
    label: z.string().optional(),
    tags: Tags.optional(),
    from: ValidZDate.optional(),
    to: ValidZDate.optional(),
    token: IToken.optional(),
    chain: IChain.optional(),
    sandbox: Bool.optional(),
});
export type IPaymentsFiltersBase = z.infer<typeof IPaymentsFiltersBase>;

export const IRequestForOrgPayments = IPaymentsFiltersBase.extend({
    pagination_key: IRequestForPaymentsPaginationKey.optional(),
    sort_by: PaymentSortSpec.optional(),
    offset: NonNegativeNumeric.optional(),
}).superRefine((data, ctx) => {
    if (data.pagination_key && (data.offset !== undefined || data.sort_by !== undefined)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "pagination_key cannot be combined with offset or sort_by",
            path: ["pagination_key"],
        });
    }
});
export type IRequestForOrgPayments = z.infer<typeof IRequestForOrgPayments>;

export const IRequestForUserPayments = IPaymentsFiltersBase.extend({
    owner_address: Address,
    pagination_key: IRequestForPaymentsPaginationKey.optional(),
});
export type IRequestForUserPayments = z.infer<typeof IRequestForUserPayments>;

// XXX deprecated: use WithdrawAccount instead. Remove once clients are migrated.
export const WorkflowAccount = z.enum(["intent", "spw", "rdw"]);
export type WorkflowAccount = z.infer<typeof WorkflowAccount>;

export const ISignatureType = z.enum(["EIP712", "EIP191"]);
export type ISignatureType = z.infer<typeof ISignatureType>;

export const IRequestForWithdrawPayload = z.object({
    workflow_id: z.uuid(),
    token_amounts: z.array(ITokenAmount),
    recipient_address: Address,
    accept_blame: Bool.optional().default(true),
    // XXX deprecated: use withdraw_account instead. Remove once clients are migrated.
    account_type: WorkflowAccount.optional(),
    withdraw_account: WithdrawAccount.optional(),
});
export type IRequestForWithdrawPayload = z.infer<
    typeof IRequestForWithdrawPayload
>;

export const IWithdrawPayloadResponse = z.object({
    withdraw_id: z.uuid(),
    // XXX deprecated: use signature_type instead. Remove once clients are migrated.
    type: z.enum(["eip712", "erc191"]),
    signature_type: ISignatureType,
    workflow_id: z.string(),
    payload: z.string(),
    state_token: z.string(),
    submit_by: ZDate.optional(),
});
export type IWithdrawPayloadResponse = z.infer<typeof IWithdrawPayloadResponse>;

// XXX deprecated: use IRequestForWithdrawWithStateToken instead. Remove once clients are migrated.
export const IRequestForWithdrawLegacy = z.object({
    workflow_id: z.uuid(),
    token_amounts: z.array(ITokenAmount),
    recipient_address: Address,
    accept_blame: Bool.optional().default(true),
    signature: Signature,
    payload: z.string().optional(),
    account_type: WorkflowAccount.optional(),
    withdraw_account: WithdrawAccount.optional(),
    polling_timeout: z.number().int().nonnegative().max(300_000).optional().default(300_000),
});
export type IRequestForWithdrawLegacy = z.infer<typeof IRequestForWithdrawLegacy>;

export const IRequestForWithdrawWithStateToken = z.object({
    signature: Signature,
    state_token: z.string(),
    polling_timeout: z.number().int().nonnegative().max(300_000).optional().default(300_000),
});
export type IRequestForWithdrawWithStateToken = z.infer<typeof IRequestForWithdrawWithStateToken>;

// The IRequestForWithdrawLegacy arm of this union is XXX deprecated; remove once clients are
// migrated to IRequestForWithdrawWithStateToken.
export type IRequestForWithdraw = IRequestForWithdrawLegacy | IRequestForWithdrawWithStateToken;

export const IWithdrawResponse = z.object({
    workflow_id: z.string(),
    tx_id: TxId,
});
export type IWithdrawResponse = z.infer<typeof IWithdrawResponse>;

export const IPaymentChoice = z.object({
    payment_id: z.uuid(),
    state_token: z.string(),
    owner_chain: IChain.optional(),
    owner_address: Address,
    destination_address: Address,
});
export type IPaymentChoice = z.infer<typeof IPaymentChoice>;

export const IVerificationType = z.enum(["OWNER_VERIFY", "SIMULATE_WITHDRAWAL", "RDW_AUTHORIZATION", "FUNDING_VERIFY"]);
export type IVerificationType = z.infer<typeof IVerificationType>;

export const IVerificationReason = z.enum([
    "EVM_OWNER",
    "EVM_WITHDRAWAL",
    "EVM_RDW_AUTHORIZATION",
    "HYPERCORE_SEND",
    "EVM_GASLESS_AUTH",
]);
export type IVerificationReason = z.infer<typeof IVerificationReason>;

export const IProviderSdkConfig = z.object({
    params: z.record(z.string(), z.unknown()), // TODO: update when integrating Stripe SDK
});
export type IProviderSdkConfig = z.infer<typeof IProviderSdkConfig>;

export const IOnrampCaptureLink = z.object({
    url: HttpsUrl,
    post_message_origin: HttpsUrl,
    redirect_url: SafeUrl.optional(),
});
export type IOnrampCaptureLink = z.infer<typeof IOnrampCaptureLink>;

export const IOnrampCapture = z.object({
    payin_methods: z.array(PayinMethod),
}).and(
    z.discriminatedUnion("surface", [
        z.object({ surface: z.literal(CaptureSurface.enum.PROVIDER_SDK), sdk: IProviderSdkConfig }),
        z.object({ surface: z.literal(CaptureSurface.enum.PROVIDER_LINK), link: IOnrampCaptureLink }),
    ]),
);
export type IOnrampCapture = z.infer<typeof IOnrampCapture>;

export const IOnrampInstruction = z.object({
    type: z.literal("ONRAMP"),
    payment_id: z.string(),
    funding_page_url: HttpsUrl,
    deposit_info: z.array(z.object({
        deposit_token: IToken,
        deposit_amount: Amount,
        deposit_address: Address,
        deposit_chain: IChain,
    })),
    supersedable_payment_summaries: z.array(ISupersedablePaymentSummary).optional(),
});
export type IOnrampInstruction = z.infer<typeof IOnrampInstruction>;

export const ITransferInInstruction = z.object({
    type: z.literal("TRANSFER_IN"),
    payment_id: z.string(),
    funding_page_url: HttpsUrl,
    deposit_info: z.array(z.object({
        deposit_token: IToken,
        deposit_amount: Amount,
        deposit_address: Address,
        deposit_chain: IChain,
    })),
});
export type ITransferInInstruction = z.infer<typeof ITransferInInstruction>;

export const IUserVerifyInstruction = z.object({
    type: z.literal("USER_VERIFY"),
    verification_token: z.string(),
    verifications: z.array(z.object({
        reason: IVerificationReason,
        signature_type: ISignatureType,
        payload: z.string(),
        chain_id: z.number().optional(),
    })),
});
export type IUserVerifyInstruction = z.infer<typeof IUserVerifyInstruction>;

export const KycStanding = z.enum([
    "CLEARED",
    "AWAITING_PROVIDER",
    "ACTION_REQUIRED",
    "ATTEMPTABLE",
    "REFUSED",
]);
export type KycStanding = z.infer<typeof KycStanding>;

export const KycFormProperty = z.object({
    type: z.literal("string"),
    title: z.string(),
    description: z.string().optional(),
    format: z.enum(["date", "email", "uri"]).optional(),
    enum: z.array(z.string()).optional(),
    minLength: z.int().nonnegative().optional(),
    maxLength: z.int().positive().optional(),
});
export type KycFormProperty = z.infer<typeof KycFormProperty>;

export const KycFormSchema = z.object({
    type: z.literal("object"),
    properties: z.record(z.string(), KycFormProperty),
    required: z.array(z.string()).default([]),
});
export type KycFormSchema = z.infer<typeof KycFormSchema>;

export const KycFormSpec = z.object({
    schema: KycFormSchema,
    provider_ref: z.string(),
});
export type KycFormSpec = z.infer<typeof KycFormSpec>;

export const KycAction = z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("FORM"), form: KycFormSpec }),
    z.object({ kind: z.literal("PHONE_OTP"), phone_number: z.e164() }),
    z.object({ kind: z.literal("REDIRECT"), url: HttpsUrl }),
    z.object({ kind: z.literal("SDK"), token: z.string() }),
    z.object({ kind: z.literal("WAIT"), retry_after_seconds: z.int().positive() }),
]);
export type KycAction = z.infer<typeof KycAction>;

export const KycInstruction = z.object({
    type: z.literal("KYC"),
    provider_id: z.string(),
    track: z.string(),
    action: KycAction,
});
export type KycInstruction = z.infer<typeof KycInstruction>;

export const ISavedPayoutMethod = z.object({
    payment_method_ref: z.string(),
    payout_method: PayoutMethod.optional(),
    category: z.string().optional(),
    country_alpha3: CountryAlpha3.optional(),
    beneficiary: z.string().optional(),
    account_last4: z.string().optional(),
    archived_at: ZDate.nullable().optional(),
});
export type ISavedPayoutMethod = z.infer<typeof ISavedPayoutMethod>;

export const IPayoutMethodInstruction = z.object({
    type: z.literal("PAYOUT_METHOD"),
    payment_id: z.string(),
    offramp: RampName,
    payout_methods: z.array(ISavedPayoutMethod),
});
export type IPayoutMethodInstruction = z.infer<typeof IPayoutMethodInstruction>;

export const IContractCallInstruction = z.object({
    type: z.literal("CONTRACT_CALL"),
    payment_id: z.string(),
    call: z.object({
        chain: IChain,
        to: Address,
        data: z.string(),
        value: Amount,
    }),
});
export type IContractCallInstruction = z.infer<typeof IContractCallInstruction>;

export const IInstruction = z.discriminatedUnion("type", [
    IOnrampInstruction,
    ITransferInInstruction,
    IUserVerifyInstruction,
    IContractCallInstruction,
    KycInstruction,
    IPayoutMethodInstruction,
]);
export type IInstruction = z.infer<typeof IInstruction>;

export const IPersonRequest = z.object({
    sandbox: Bool.optional(),
    flags: z.array(Flag).optional(),
});
export type IPersonRequest = z.infer<typeof IPersonRequest>;

export const IUpdatePersonRequest = IPersonRequest.extend({ phone_jwt: z.string().min(1).optional() });
export type IUpdatePersonRequest = z.infer<typeof IUpdatePersonRequest>;

export const IPersonResponse = z.object({
    person_id: z.uuid(),
    kyc_providers: z.record(z.string(), z.record(z.string(), KycStanding)),
});
export type IPersonResponse = z.infer<typeof IPersonResponse>;

export const IKycInitiationRequest = z.object({
    provider_id: z.string().optional(),
    sandbox: Bool.optional(),
    flags: z.array(Flag).optional(),
});
export type IKycInitiationRequest = z.infer<typeof IKycInitiationRequest>;

export const IKycInitiationResponse = z.object({
    status: KycStanding,
    instruction: KycInstruction.optional(),
});
export type IKycInitiationResponse = z.infer<typeof IKycInitiationResponse>;

export const WithdrawTaskStatus = z.enum(["PENDING", "SUCCESS", "FAILED", "EXPIRED"]);
export type WithdrawTaskStatus = z.infer<typeof WithdrawTaskStatus>;

export const IWithdrawalStatus = z.object({
    status: WithdrawTaskStatus,
    token_amounts: z.array(ITokenAmount),
    recipient_address: z.string(),
    transaction_hash: z.string().nullable(),
    issues: z.array(IIssue).nullable(),
});
export type IWithdrawalStatus = z.infer<typeof IWithdrawalStatus>;

export const IPaymentStatus = z.object({
    payment_id: z.string(),
    org_id: OrgId,
    status: WorkflowOverallStatus,
    funded: z.boolean(),
    created_at: ZDate,
    updated_at: ZDate,
    initiate_fund_by: ZDate,
    completed_at: ZDate.optional(),
    quote_request: IQuoteRequest.optional(),
    quoted: IQuotedEntry,
    fulfilled: IFulfilledEntry,
    current_prices: IPrices,
    price_currency: IFiat,
    customer_id: z.string().optional(),
    label: z.string().optional(),
    tags: Tags.optional(),
    payout_destination: z.object({
        payout_method: PayoutMethod.optional(),
        beneficiary: z.string().optional(),
        account_last4: z.string().optional(),
    }).nullable().optional(),
    processing_addresses: z.array(z.object({
        chain: z.string(),
        address: Address,
    })),
    owner_chain: IChain,
    owner_address: Address,
    funder_address: Address.optional(),
    destination_address: Address.nullable(),
    next_instruction: IInstruction.optional(),
    issues: z.array(IIssue).optional(),
    withdrawals: z.array(IWithdrawalStatus).nullable(),
    parent_payment_id: z.uuid().optional(),
    client_redirect_url: SafeUrl.nullable(),
    geolocation: z.object({
        alpha3_country_code: CountryAlpha3,
        state_code: z.union([StateAlpha2, z.literal("")]),
    }).nullable(),
    fee_sponsorship_state: IFeeSponsorshipState.nullable(),
});
export type IPaymentStatus = z.infer<typeof IPaymentStatus>;

export const IPaymentStatusesResults = z.object({
    payment_statuses: z.array(IPaymentStatus),
    next_pagination_key: z.string().nullable(),
    total_payments: z.number().int().nonnegative(),
});
export type IPaymentStatusesResults = z.infer<typeof IPaymentStatusesResults>;

export const ClientRedirectAfter = z.enum(["COMPLETED", "FUNDED"]);
export type ClientRedirectAfter = z.infer<typeof ClientRedirectAfter>;

const KycSubmissionValues = z.record(z.string(), z.string().max(1024)).refine(
    (values) => Object.keys(values).length <= 64,
    { message: "Too many fields" },
);

export const KycSubmission = z.object({
    provider_id: z.string(),
    track: z.string(),
    provider_ref: z.string(),
    fields: KycSubmissionValues,
});
export type KycSubmission = z.infer<typeof KycSubmission>;

export const IInitialConfirmPaymentRequest = z.object({
    payment_id: z.uuid(),
    state_token: z.string(),
    owner_chain: z.literal("ethereum").optional(),
    owner_address: Address,
    funder_address: Address.optional(),
    destination_address: Address.optional(),
    client_redirect_url: SafeUrl.optional(),
    client_redirect_after: ClientRedirectAfter.optional().default("COMPLETED"),
    allow_unsafe_owner: z.boolean().optional(),
    simulate_withdrawal: z.boolean().optional(),
    payment_expiration_minutes: z.number().int().positive().max(60).optional(),
    kyc_submission: KycSubmission.optional(),
});
export type IInitialConfirmPaymentRequest = z.infer<typeof IInitialConfirmPaymentRequest>;

export const IContinueConfirmPaymentRequest = z.object({
    verification_token: z.string(),
    signatures: z.array(z.object({
        reason: IVerificationReason,
        signature: z.string(),
    })),
});
export type IContinueConfirmPaymentRequest = z.infer<typeof IContinueConfirmPaymentRequest>;

export const IConfirmPaymentRequest = z.union([
    IInitialConfirmPaymentRequest,
    IContinueConfirmPaymentRequest,
]);
export type IConfirmPaymentRequest = z.infer<typeof IConfirmPaymentRequest>;

export const IConfirmPaymentResponse = z.union([IPaymentStatus, z.object({ next_instruction: IInstruction })]);
export type IConfirmPaymentResponse = z.infer<typeof IConfirmPaymentResponse>;

export const IPaymentStatusRequest = z.object({
    payment_id: z.uuid(),
    enable_withdraw_pending: z.string().optional(),
});
export type IPaymentStatusRequest = z.infer<typeof IPaymentStatusRequest>;

export const IOnrampSessionRequest = z.object({
    payment_id: z.uuid(),
    redirect_url: HttpsUrl.optional(),
    referrer_domain: HttpsUrl.optional(),
    embed_url: SafeUrl.optional(),
    terms_accepted_at: ValidZDate.optional(),
});
export type IOnrampSessionRequest = z.infer<typeof IOnrampSessionRequest>;

export const IHostedOnrampSession = z.object({
    kind: z.literal("HOSTED"),
    funding_page_onramp_url: HttpsUrl,
    client_secret: z.string().optional(),
    client_redirect_url: SafeUrl.nullable(), // XXX delete when FE migrates
    onramp_redirect_url: SafeUrl,
});
export type IHostedOnrampSession = z.infer<typeof IHostedOnrampSession>;

/** @deprecated Hosted-only view of the /onramp response; read IOnrampSessionResponse and branch on `kind`. */
export const IOnrampSessionResult = IHostedOnrampSession;
export type IOnrampSessionResult = z.infer<typeof IOnrampSessionResult>;

export const ICountry = z.union([CountryAlpha2, CountryAlpha3]);
export type ICountry = z.infer<typeof ICountry>;

export const IHostedPayoutMethodSessionRequest = z.object({
    kind: z.literal("HOSTED").default("HOSTED"),
    offramp: RampName,
    payout_method: PayoutMethod,
    redirect_url: HttpsUrl,
    sandbox: Bool.default(false),
    fiat: z.string().optional(),
    country: ICountry.optional(),
    referrer_domain: HttpsUrl.optional(),
});
export type IHostedPayoutMethodSessionRequest = z.infer<
    typeof IHostedPayoutMethodSessionRequest
>;

export const IFormPath = z.string().regex(/^[A-Za-z0-9_]+(\.[A-Za-z0-9_]+)*$/);
export type IFormPath = z.infer<typeof IFormPath>;

export const IHeadlessPayoutMethodRequest = z.object({
    kind: z.literal("HEADLESS"),
    offramp: RampName,
    payout_method: PayoutMethod,
    sandbox: Bool.default(false),
    fiat: z.string().optional(),
    country: ICountry.optional(),
    form_token_id: z.uuid(),
    clear_fields: z.record(IFormPath, z.string()),
});
export type IHeadlessPayoutMethodRequest = z.infer<typeof IHeadlessPayoutMethodRequest>;

export const IPayoutMethodSessionRequest = z.union([
    IHeadlessPayoutMethodRequest,
    IHostedPayoutMethodSessionRequest,
]);
export type IPayoutMethodSessionRequest = z.infer<
    typeof IPayoutMethodSessionRequest
>;

export const IHostedPayoutMethodSessionResult = z.object({
    kind: z.literal("HOSTED"),
    hosted_session_url: HttpsUrl,
    external_id: z.string(),
    redirect_url: SafeUrl,
});
export type IHostedPayoutMethodSessionResult = z.infer<
    typeof IHostedPayoutMethodSessionResult
>;

export const IHeadlessPayoutMethodResult = z.object({
    kind: z.literal("HEADLESS"),
    payout_method: ISavedPayoutMethod,
});
export type IHeadlessPayoutMethodResult = z.infer<
    typeof IHeadlessPayoutMethodResult
>;

export const IPayoutMethodSessionResult = z.discriminatedUnion("kind", [
    IHostedPayoutMethodSessionResult,
    IHeadlessPayoutMethodResult,
]);
export type IPayoutMethodSessionResult = z.infer<
    typeof IPayoutMethodSessionResult
>;

export const IListPayoutMethodsRequest = z.object({
    offramp: RampName,
    sandbox: Bool.default(false),
    include_archived: Bool.optional(),
});
export type IListPayoutMethodsRequest = z.infer<typeof IListPayoutMethodsRequest>;

export const ISetPayoutMethodArchivedRequest = z.object({
    offramp: RampName,
    sandbox: Bool.default(false),
    payment_method_ref: z.string(),
    archive: z.boolean(),
});
export type ISetPayoutMethodArchivedRequest = z.infer<typeof ISetPayoutMethodArchivedRequest>;

export const IListPayoutMethodsResponse = z.object({
    payout_methods: z.array(ISavedPayoutMethod),
});
export type IListPayoutMethodsResponse = z.infer<typeof IListPayoutMethodsResponse>;

export const IHeadlessOnrampSession = z.object({
    kind: z.literal("HEADLESS"),
    provider: RampName,
    capture: IOnrampCapture,
});
export type IHeadlessOnrampSession = z.infer<typeof IHeadlessOnrampSession>;

export const IOnrampSessionResponse = z.discriminatedUnion("kind", [IHostedOnrampSession, IHeadlessOnrampSession]);
export type IOnrampSessionResponse = z.infer<typeof IOnrampSessionResponse>;

export const IPayoutMethodFormsRequest = z.object({
    offramp: RampName,
    fiat: z.string().min(1),
    country: ICountry.optional(),
    sandbox: Bool.default(false),
});
export type IPayoutMethodFormsRequest = z.infer<typeof IPayoutMethodFormsRequest>;

export const IPayoutMethodForm = z.object({
    payout_method: PayoutMethod,
    schema: z.record(z.string(), z.unknown()),
    content_hash: z.string().optional(),
});
export type IPayoutMethodForm = z.infer<typeof IPayoutMethodForm>;

export const IPayoutMethodFormsResponse = z.object({
    resolved_country: CountryAlpha2.nullable(),
    payout_countries: z.array(CountryCode),
    forms: z.array(IPayoutMethodForm),
});
export type IPayoutMethodFormsResponse = z.infer<typeof IPayoutMethodFormsResponse>;

export const IWithdrawType = z.enum(["PAYMENT", "EXTRA_RDW_BALANCE"]);
export type IWithdrawType = z.infer<typeof IWithdrawType>;

export const IPaymentWithdrawAuthorizationRequest = z.object({
    withdraw_type: z.literal("PAYMENT").optional().default("PAYMENT"),
    payment_id: z.uuid(),
    token_amounts: z.array(ITokenAmount),
    recipient_address: Address,
    // XXX deprecated: use withdraw_account instead. Remove once clients are migrated.
    account_type: WorkflowAccount.optional(),
    withdraw_account: WithdrawAccount.optional(),
});
export type IPaymentWithdrawAuthorizationRequest = z.infer<
    typeof IPaymentWithdrawAuthorizationRequest
>;

export const IExtraRDWBalanceWithdrawAuthorizationRequest = z.object({
    withdraw_type: z.literal("EXTRA_RDW_BALANCE"),
    rdw_address: Address,
    token_amounts: z.array(ITokenAmount),
    recipient_address: Address,
});
export type IExtraRDWBalanceWithdrawAuthorizationRequest = z.infer<
    typeof IExtraRDWBalanceWithdrawAuthorizationRequest
>;

export const IWithdrawPaymentAuthorizationRequest = z.object({
    withdraw_from: IParkedFundWithdrawSource,
    token_amounts: z.array(ITokenAmount),
    recipient_address: Address,
});
export type IWithdrawPaymentAuthorizationRequest = z.infer<
    typeof IWithdrawPaymentAuthorizationRequest
>;

export const IWithdrawPaymentAuthorizationResponse = z.object({
    // XXX deprecated: use signature_type instead. Remove once clients are migrated.
    type: z.enum(["eip712", "erc191"]).optional(),
    signature_type: ISignatureType,
    // XXX deprecated: track the withdrawal by withdraw_id instead.
    payment_id: z.string().optional(),
    withdraw_id: z.uuid(),
    withdraw_authorization: z.string(),
    state_token: z.string(),
    submit_by: ZDate.optional(),
});
export type IWithdrawPaymentAuthorizationResponse = z.infer<
    typeof IWithdrawPaymentAuthorizationResponse
>;

// XXX deprecated: use IWithdrawPaymentRequestWithStateToken instead. Remove once clients are migrated.
export const IWithdrawPaymentRequestLegacy = z.object({
    payment_id: z.uuid(),
    token_amounts: z.array(ITokenAmount),
    recipient_address: Address,
    owner_signature: Signature,
    payload: z.string().optional(),
    // XXX deprecated: use withdraw_account instead. Remove once clients are migrated.
    account_type: WorkflowAccount.optional(),
    withdraw_account: WithdrawAccount.optional(),
    polling_timeout: z.number().int().nonnegative().max(300_000).optional().default(300_000),
});
export type IWithdrawPaymentRequestLegacy = z.infer<typeof IWithdrawPaymentRequestLegacy>;

export const IWithdrawPaymentRequestWithStateToken = z.object({
    signature: Signature,
    state_token: z.string(),
    polling_timeout: z.number().int().nonnegative().max(300_000).optional().default(300_000),
});
export type IWithdrawPaymentRequestWithStateToken = z.infer<typeof IWithdrawPaymentRequestWithStateToken>;

export type IWithdrawPaymentRequest = IWithdrawPaymentRequestLegacy | IWithdrawPaymentRequestWithStateToken;

export const IWithdrawStatusRequest = z.object({ withdraw_id: z.uuid() });
export type IWithdrawStatusRequest = z.infer<typeof IWithdrawStatusRequest>;

export const IWithdrawStatusResponse = z.object({
    withdraw_id: z.uuid(),
    status: WithdrawTaskStatus,
    transaction_hash: TxId.nullable(),
    // XXX deprecated: track the withdrawal by withdraw_id instead.
    payment_id: z.string().optional(),
    submit_by: ZDate.optional(),
});
export type IWithdrawStatusResponse = z.infer<typeof IWithdrawStatusResponse>;

export const IWithdrawPaymentResponse = IWithdrawStatusResponse;
export type IWithdrawPaymentResponse = IWithdrawStatusResponse;
export const IWithdrawConfirmResponse = IWithdrawStatusResponse;
export type IWithdrawConfirmResponse = z.infer<typeof IWithdrawConfirmResponse>;

export const IBalanceTarget = z.object({
    address: Address,
    token: IToken,
});
export type IBalanceTarget = z.infer<typeof IBalanceTarget>;

export const IOwnerScope = z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("PAYMENT"), payment_id: z.uuid() }),
    z.object({ kind: z.literal("EXTRA") }),
]);
export type IOwnerScope = z.infer<typeof IOwnerScope>;

export const IExternalBalanceQuery = z.object({
    source: z.literal("EXTERNAL"),
    targets: z.array(IBalanceTarget),
});
export type IExternalBalanceQuery = z.infer<typeof IExternalBalanceQuery>;

export const IOwnerBalanceQuery = z.object({
    source: z.literal("OWNER"),
    owner: Address,
    scope: IOwnerScope,
    tokens: z.array(IToken).optional(),
});
export type IOwnerBalanceQuery = z.infer<typeof IOwnerBalanceQuery>;

export const IBalanceQuery = z.discriminatedUnion("source", [IExternalBalanceQuery, IOwnerBalanceQuery]);
export type IBalanceQuery = z.infer<typeof IBalanceQuery>;

export const IRequestForPaymentBalances = z.object({
    query: IBalanceQuery,
});
export type IRequestForPaymentBalances = z.infer<typeof IRequestForPaymentBalances>;

export const IExternalBalanceValue = z.union([
    z.object({ kind: z.literal("amount"), amount: Amount }),
    z.object({ kind: z.literal("error") }),
]);
export type IExternalBalanceValue = z.infer<typeof IExternalBalanceValue>;

export const IOwnerBalanceValue = z.union([
    z.object({
        kind: z.literal("amount"),
        amount: Amount,
        withdrawal_fee: Amount.nullable(),
        min_withdrawal_amount: Amount.nullable(),
    }),
    z.object({ kind: z.literal("error") }),
]);
export type IOwnerBalanceValue = z.infer<typeof IOwnerBalanceValue>;

export const IExternalBalanceResult = IBalanceTarget.extend({
    value: IExternalBalanceValue,
});
export type IExternalBalanceResult = z.infer<typeof IExternalBalanceResult>;

export const IOwnerBalanceResult = IBalanceTarget.extend({
    value: IOwnerBalanceValue,
    withdraw_from: IParkedFundWithdrawSource.nullable(),
    payment_gist: IPaymentGist.nullable(),
});
export type IOwnerBalanceResult = z.infer<typeof IOwnerBalanceResult>;

export const IOwnerFutureWithdrawableBalance = IOwnerBalanceResult.extend({
    deadline: ZDate,
});
export type IOwnerFutureWithdrawableBalance = z.infer<typeof IOwnerFutureWithdrawableBalance>;

export const IExternalBalancesResponse = z.object({
    balance_results: z.array(IExternalBalanceResult),
});
export type IExternalBalancesResponse = z.infer<typeof IExternalBalancesResponse>;

export const IOwnerBalancesResponse = z.object({
    balance_results: z.array(IOwnerBalanceResult),
    future_withdrawable_balances: z.array(IOwnerFutureWithdrawableBalance).default([]),
});
export type IOwnerBalancesResponse = z.infer<typeof IOwnerBalancesResponse>;

export const IPaymentIssuesRequest = z.object({
    owner_address: Address,
});
export type IPaymentIssuesRequest = z.infer<typeof IPaymentIssuesRequest>;

export const IPaymentIssuesResponse = z.object({
    issues: z.array(IIssue),
});
export type IPaymentIssuesResponse = z.infer<typeof IPaymentIssuesResponse>;

export const ITransferTransactionRequest = z.object({
    token: IToken,
    amount: Amount,
    sender: Address,
    destination: Address,
});
export type ITransferTransactionRequest = z.infer<typeof ITransferTransactionRequest>;

export const ITransferTransactionResponse = z.object({
    transaction: z.string(),
    warning: z.string().optional(),
});
export type ITransferTransactionResponse = z.infer<typeof ITransferTransactionResponse>;

export const Login = z.record(z.string(), z.any()).nullable();
export type Login = z.infer<typeof Login>;

export const EIP712TypedData = z.object({
    domain: z.record(z.string(), z.any()),
    types: z.record(
        z.string(),
        z.array(z.object({
            name: z.string(),
            type: z.string(),
        })),
    ),
    primaryType: z.string(),
    message: z.record(z.string(), z.any()),
});
export type EIP712TypedData = z.infer<typeof EIP712TypedData>;

export const IVerifyOwnershipRequest = z.object({
    owner_address: Address,
    typed_data: EIP712TypedData,
    signature: Signature,
    login: Login.optional(),
});
export type IVerifyOwnershipRequest = z.infer<typeof IVerifyOwnershipRequest>;

export const IVerifyOwnershipResponse = z.object({});
export type IVerifyOwnershipResponse = z.infer<typeof IVerifyOwnershipResponse>;

export const IGetWalletInfoRequest = z.object({
    address: Address,
});
export type IGetWalletInfoRequest = z.infer<typeof IGetWalletInfoRequest>;

export const IGetWalletInfoResponse = z.object({
    verified: z.boolean(),
    login: z.array(z.string()).nullable(),
}).nullable();
export type IGetWalletInfoResponse = z.infer<typeof IGetWalletInfoResponse>;

export const IOrgStatsRequest = z.object({
    from: ValidZDate.optional(),
    to: ValidZDate.optional(),
    deposit_labels: z.array(z.string()),
    withdrawal_labels: z.array(z.string()),
    sandbox: Bool.optional(),
    timezone: Timezone.optional(),
});
export type IOrgStatsRequest = z.infer<typeof IOrgStatsRequest>;

const IOrdersTimeseriesPoint = z.object({
    bucket_start: ZDate,
    volume_usd: Amount,
    order_count: z.number().int(),
});

const IDepositsWithdrawalsPoint = z.object({
    bucket_start: ZDate,
    deposits_usd: Amount,
    withdrawals_usd: Amount,
});

const IOrdersTimeseries = z.object({
    daily: z.array(IOrdersTimeseriesPoint),
    monthly: z.array(IOrdersTimeseriesPoint),
    yearly: z.array(IOrdersTimeseriesPoint),
});

const IDepositsWithdrawalsTimeseries = z.object({
    daily: z.array(IDepositsWithdrawalsPoint),
    monthly: z.array(IDepositsWithdrawalsPoint),
    yearly: z.array(IDepositsWithdrawalsPoint),
});

const ITopPaymentMethod = z.discriminatedUnion("kind", [
    z.object({
        kind: z.literal("crypto"),
        token: IToken,
        chain: IChain,
        volume_usd: Amount,
        order_count: z.number().int(),
        share: Amount,
    }),
    z.object({
        kind: z.literal("fiat"),
        ramp_name: RampName.nullable(),
        volume_usd: Amount,
        order_count: z.number().int(),
        share: Amount,
    }),
]);

const ITopAsset = z.object({
    token: IToken,
    chain: IChain,
    volume_usd: Amount,
    order_count: z.number().int(),
    share: Amount,
});

const ITopMarket = z.object({
    alpha3_country_code: CountryAlpha3,
    volume_usd: Amount,
    unique_wallets: z.number().int(),
    order_count: z.number().int(),
    share: Amount,
});

const IOtherBucket = z.object({
    volume_usd: Amount,
    order_count: z.number().int(),
    share: Amount,
});

export const IOrgStatsResponse = z.object({
    totals: z.object({
        volume_usd: Amount,
        order_count: z.number().int(),
        unique_wallets: z.number().int(),
    }),
    orders_timeseries: IOrdersTimeseries,
    deposits_vs_withdrawals: IDepositsWithdrawalsTimeseries,
    top_payment_methods: z.object({
        entries: z.array(ITopPaymentMethod),
        other: IOtherBucket.nullable(),
    }),
    top_assets_acquired: z.object({
        entries: z.array(ITopAsset),
        other: IOtherBucket.nullable(),
    }),
    top_markets: z.array(ITopMarket),
});
export type IOrgStatsResponse = z.infer<typeof IOrgStatsResponse>;

export const WebhookEventType = z.enum(["WORKFLOW_COMPLETED", "WORKFLOW_FAILED"]);
export type WebhookEventType = z.infer<typeof WebhookEventType>;

export const WebhookEvent = z.object({
    id: z.uuid(),
    time: ZDate,
    event_type: WebhookEventType,
    event: z.object({
        workflow_id: z.uuid(),
        status: WorkflowOverallStatusStored,
        tags: Tags.optional(),
    }),
    sample: z.boolean().default(false),
});
export type WebhookEvent = z.infer<typeof WebhookEvent>;
